import { FC, Fragment, useEffect } from "react";
import { BookingProduct, BookingTable, CustomCSSProperties, LayoutLocation, TableObject, TranslationKey } from "../../types";
import { Icon } from "../../parts/Icon";
import { useApp } from "../../context/useApp";
import { removeDuplicates, _, getTableVars } from "../../utils/helpers";
import { UNASIGNED_ZONES } from "../../utils/symbols";

interface TableProps {
    showAvailability?: boolean
    table: TableObject
    onTableClick?: (table: BookingTable, product: BookingProduct | undefined, layoutLocation: LayoutLocation) => void
}

export const Table: FC<TableProps> = ({ table, onTableClick, showAvailability }) => {

    const { state: { booking, shifts, products } } = useApp()

    const thisTableInBooking = booking.getTable(table.id)
    const isOff = booking.getUnusuedSeats() > 0 && !thisTableInBooking

    const disable = (() => {
        if (!showAvailability) return false

        const shift = shifts.find(shift => shift.id === booking.shiftId)
        if (booking.shiftId === 0 || !shift) return false

        if (table.occupied === 1 && !UNASIGNED_ZONES.includes(table.zone)) return true
        if (table.zone === 'za' && !shift.zaOpen) return true
        if (table.zone === 'zb' && !shift.zbOpen) return true
        if (table.zone === 'zc' && !shift.zcOpen) return true
        if (table.zone === 'zv' && !shift.zvOpen) return true

    })()

    const handleSeatClick = (layoutLocation: LayoutLocation, product: BookingProduct | undefined) => {

        const seat = layoutLocation[1]

        let seats = thisTableInBooking ? thisTableInBooking.seats : []

        //add or remove seat
        if (product) seats = seats.filter(selectedSeat => selectedSeat !== seat)
        else seats.push(seat)
        seats = removeDuplicates(seats) as number[]

        if (onTableClick) onTableClick(
            {
                id: table.id,
                name: table.name,
                zone: table.zone,
                occupied: seats.length,
                pax: table.pax,
                seats
            },
            product,
            layoutLocation
        )
    }

    const unSelectTable = () => {
        if (!thisTableInBooking) return
        for (let seat of thisTableInBooking.seats) {
            const layoutLocation: LayoutLocation = [table.id, seat]
            const seatTicket = booking.getTicketByLayoutLocation(table.id, seat)
            const product = products.find(product => product.id === seatTicket?.productId)
            if (product) booking.handleProduct('remove', product, layoutLocation)
        }
        booking.handleTable({
            id: table.id,
            name: table.name,
            zone: table.zone,
            pax: table.pax,
            occupied: 0,
            seats: []
        }).render()
    }

    const { side, row, col } = getTableVars(table.name)

    useEffect(() => {
        if (thisTableInBooking && disable) {
            unSelectTable()
        }
        // eslint-disable-next-line
    }, [])

    return (
        <li
            id={table.name}
            role="group"
            tabIndex={0}
            aria-label={`${_('0ar_table__')} ${table.name}. ${table.pax} ${_('0ar_seats__')}. ${_('0ar_row____', row)}. ${_('0ar_col____', col)}. ${_(`0ar_${side.padEnd(7, '_')}` as TranslationKey)}. ${thisTableInBooking?.seats.length ?? 0} ${_('0ar_selec__')}`}
        >
            <ul
                id={`${table.name}-seats`}
                role="group"
                className={`table-container p-a zone-${table.zone} df ${table.style === 'v' ? 'vertical' : 'horizontal'} p-r pax-${table.pax} ${table.access > 0 ? 'accesible' : ''} ${isOff ? 'off' : ''}`}
                style={{ '--x': `${table.x}px`, '--y': `${table.y}px`, '--pax': `${table.pax}` } as CustomCSSProperties}
            >
                {Array.from({ length: table.pax }).map((x, index) => {

                    const thisSeat = index + 1
                    const thisSeatTicket = booking.getTicketByLayoutLocation(table.id, thisSeat)
                    const isChildren = !!(thisSeatTicket && thisSeatTicket.category === 'children')

                    return (
                        <Fragment key={index} >
                            <li
                                role="button"
                                className={`table h-50p w-50p df aic jcc p-r h-50p w-50p df aic jcc p-r ${thisSeatTicket ? 'selected' : ''} ${disable ? 'disabled' : ''}`}
                                aria-label={`${_('0ar_seat___')} ${thisSeat} ${thisSeatTicket ? _('0ar_selec__') : _('0ar_notse__')}`}
                                aria-live="assertive"
                                onClick={(event) => {
                                    if (event.currentTarget.classList.contains('disabled')) return
                                    handleSeatClick([table.id, thisSeat], thisSeatTicket)
                                }}
                            >
                                <div className={`table-content w-100 h-100 p-r df aic jcc`} aria-hidden>

                                    <Icon
                                        icon={'children'}
                                        className="m-0"
                                        aria-hidden
                                        style={{ scale: '1', opacity: isChildren ? '0.5' : '0' }}
                                    />

                                    {(index === table.pax - 1 || index === 0) && !isChildren && (
                                        <span className="zone-name text white upper s15-500 p-a zi-2" aria-hidden>
                                            {/* {getZoneName('short', table.zone, booking.journey)} */}
                                            {/* {table.id} */}
                                        </span>
                                    )}
                                    {table.access === 2 && !isChildren && (
                                        <Icon
                                            aria-hidden
                                            icon={'wheelchair'}
                                            className="zi-2 p-0 m-0 p-a"
                                        />
                                    )}
                                </div>
                                <span
                                    aria-hidden
                                    className="table-bg w-50p h-50p p-a"
                                    tabIndex={-1}
                                ></span>
                            </li>
                            {index < table.pax - 1 && (<span aria-hidden className="p-a zi-2 divisor" style={{ '--index': index + 1 } as CustomCSSProperties}></span>)}
                        </Fragment>
                    )
                })}
            </ul>
        </li>
    )
}